import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faUser } from '@fortawesome/free-solid-svg-icons';
import CommonTable from '../../../../common/table/ComonTable';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';
import apiServices from '../../../../../services/apiService';
import { exportToExcel } from '../../../../../utils/excelUtils';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import SectionTab from '../../../../common/sectiontab/SectionTab';

const VisitorTableTab = ({ visitorTypeId, tableHeading }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [allVisitors, setAllVisitors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageInfo, setPageInfo] = useState({ pageSize: 10 });

    const [statusFilter, setStatusFilter] = useState("All");

    const fetchVisitorsData = async () => {
        setLoading(true);
        try {
            const response = await apiServices({
                method: "GET",
                url: `/Residential/ResVisitor/getAllVisitor`,
            });
            if (response?.success) {
                setAllVisitors(response.data?.data || []);
            }
        } catch (error) {
            console.error("Error fetching visitors:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVisitorsData();
    }, []);

    const filteredVisitors = useMemo(() => {
        let result = (allVisitors || []).filter(v => {
            if (visitorTypeId === 1) {
                return (
                    String(v.visitorTypeId) === "1" ||
                    String(v.visitorType) === "1" ||
                    (typeof v.visitorType === 'string' && v.visitorType.toLowerCase().includes('invite') && !v.visitorType.toLowerCase().includes('non')) ||
                    (typeof v.visitType === 'string' && v.visitType.toLowerCase().includes('invite')) ||
                    (v.hostName && String(v.visitorTypeId) !== "2")
                );
            } else if (visitorTypeId === 2) {
                return (
                    String(v.visitorTypeId) === "2" ||
                    String(v.visitorType) === "2" ||
                    (typeof v.visitorType === 'string' && (v.visitorType.toLowerCase().includes('walk') || v.visitorType.toLowerCase().includes('non'))) ||
                    (typeof v.visitType === 'string' && v.visitType.toLowerCase().includes('walk')) ||
                    (!v.hostName && String(v.visitorTypeId) !== "1")
                );
            }
            return true;
        });

        if (statusFilter !== "All") {
            result = result.filter(v => v.statusName === statusFilter || v.status === statusFilter);
        }

        return result;
    }, [allVisitors, statusFilter, visitorTypeId]);

    const paginatedVisitors = useMemo(() => {
        const start = (currentPage - 1) * pageInfo.pageSize;
        return filteredVisitors.slice(start, start + pageInfo.pageSize);
    }, [filteredVisitors, currentPage, pageInfo.pageSize]);

    const totalRecords = filteredVisitors.length;
    const totalPages = Math.ceil(totalRecords / pageInfo.pageSize) || 1;

    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, pageInfo.pageSize]);

    const formatTime = (dateString) => {
        if (!dateString || dateString === "—") return "—";
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;

            const day = date.getDate().toString().padStart(2, '0');
            const month = date.toLocaleString('en-US', { month: 'short' });
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } catch (e) {
            return dateString;
        }
    };

    const handleExport = () => {
        const excelHeaders = [
            { label: "NO", key: "no" },
            { label: "VISITOR NAME", key: "name" },
            { label: "SITE NAME", key: "siteName" },
            { label: "UNIT", key: "unitName" },
            { label: "MOBILE", key: "mobileNumber" },
            { label: "PURPOSE", key: "purposeOfVisit" },
            { label: "DATE", key: "createdDate" },
            { label: "STATUS", key: "statusName" }
        ];

        const exportData = filteredVisitors.map((v, i) => ({
            no: i + 1,
            siteName: v.siteName,
            unitName: v.unitName,
            name: v.name || v.visitorName,
            mobileNumber: v.mobileNumber || v.visitorMobileNumber,
            purposeOfVisit: v.purposeOfVisit,
            createdDate: formatTime(v.expectedCheckIn || v.createdDate),
            statusName: v.statusName || v.status
        }));

        exportToExcel(exportData, excelHeaders, `Visitor_Records_${visitorTypeId === 1 ? 'Invited' : 'Walkin'}`);
    };

    const tableHeaders = [
        { label: "NO", key: "no" },
        { label: "VISITOR", key: "visitorInfo" },
        // { label: "SITE ", key: "siteName" },
        { label: "UNIT", key: "unitName" },
        { label: "MOBILE", key: "visitorMobileNumber" },
        // { label: "LOCATION", key: "location" },
        { label: "PURPOSE", key: "purposeOfVisit" },
        { label: "DATE", key: "createdDate" },
        { label: "STATUS", key: "status" },
    ];

    const renderCell = (key, row) => {
        switch (key) {
            case "no":
                return <span className="text-xs font-normal text-[#000000] dark:text-textsecondary">{row.no}</span>;
            case "visitorInfo":
                return (
                    <div className="flex items-center gap-3">
                        {row.image ? (
                            <img
                                src={`${row.image}`}
                                className="w-8 h-8 rounded-full border border-gray-100 dark:border-bordercolor object-cover"
                                alt="Visitor"
                            />
                        ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-actionitembg border border-gray-200 dark:border-bordercolor flex items-center justify-center text-gray-400 dark:text-textsecondary">
                                <FontAwesomeIcon icon={faUser} className="text-xs" />
                            </div>
                        )}
                        <span className="text-xs font-bold text-[#000000] dark:text-textprimary">{row.visitorName || row.name || "—"}</span>
                    </div>
                );
            case "siteName":
            case "unitName":
            case "visitorMobileNumber":
            case "location":
            case "purposeOfVisit":
                return <span className="text-xs font-normal text-[#64748B] dark:text-textsecondary">{row[key] ?? "—"}</span>;
            case "createdDate":
                return <span className="text-xs font-normal text-[#64748B] dark:text-textsecondary">{formatTime(row.expectedCheckIn || row.createdDate)}</span>;
            case "status":
                let statusColor = "bg-gray-100 text-gray-700 dark:bg-actionitembg dark:text-textsecondary";
                const statusVal = row.statusName || row.status || "";
                const normalizedStatus = statusVal.toUpperCase().replace(/[-_ ]/g, "");

                if (normalizedStatus === "CHECKEDIN") {
                    statusColor = "bg-[#004B591A] text-[#004B59]";
                } else if (normalizedStatus === "CHECKEDOUT" || normalizedStatus === "CHECKOUT") {
                    statusColor = "bg-[#E31F262B] text-[#E31F26]";
                } else if (normalizedStatus === "PENDING") {
                    statusColor = "bg-[#FEF9C3] text-[#D97706]";
                } else if (normalizedStatus === "APPROVED") {
                    statusColor = "bg-[#DCFCE7] text-[#166534]";
                } else if (normalizedStatus === "REJECTED") {
                    statusColor = "bg-[#E31F26] text-[#FFFFFF]";
                } else if (normalizedStatus === "BLOCKED" || normalizedStatus === "BLOCK") {
                    statusColor = "bg-[#0F172A] text-white border border-[#0F172A]";
                }

                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black shadow-sm uppercase ${statusColor}`}>
                        {statusVal}
                    </span>
                );
            default:
                return <span className="text-xs font-normal text-[#000000] dark:text-textprimary">{row[key] || "—"}</span>;
        }
    };

    const headerAction = (
        <div className="flex items-center gap-3">
            <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 bg-white dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-lg text-xs font-bold text-[#0F172A] dark:text-textprimary focus:outline-none focus:ring-1 focus:border-gray-400"
            >
                <option value="All">All Status</option>
                <option value="Approved">Approved</option>
                <option value="Pending">Pending</option>
                <option value="Rejected">Rejected</option>
            </select>
            <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-[#F8FAFC] dark:bg-actionitembg border border-[#E2E8F0] dark:border-bordercolor text-[#64748B] dark:text-textsecondary text-xs font-bold rounded-lg hover:bg-white dark:hover:bg-cardbg hover:text-[#1E293B] dark:hover:text-textprimary hover:border-[#1E293B] dark:hover:border-gray-600 transition-all duration-200 shadow-sm"
            >
                <FontAwesomeIcon icon={faDownload} className=" text-sm" />
                <span>Export</span>
            </button>
        </div>
    );

    const mappedVisitorsData = paginatedVisitors.map((v, index) => ({
        ...v,
        no: ((currentPage - 1) * pageInfo.pageSize) + index + 1,
    }));

    return (
        <div className="shrink-0 flex-1 flex flex-col bg-white dark:bg-sectionheaderbg rounded-xl shadow-sm border border-gray-100 dark:border-bordercolor overflow-hidden">
            <CommonTable
                tableHeading={tableHeading}
                data={mappedVisitorsData}
                headers={tableHeaders}
                renderCell={renderCell}
                headerAction={headerAction}
                pagination={{
                    pageSize: pageInfo.pageSize,
                    pageNumber: currentPage,
                    totalPages: totalPages,
                    totalRecords: totalRecords
                }}
                loading={loading}
                onPageChange={setCurrentPage}
                onLimitChange={(newLimit) => setPageInfo(prev => ({ ...prev, pageSize: newLimit }))}
                showActionCol={false}
            />
        </div>
    );
};

const InvitedVisitorTab = () => <VisitorTableTab visitorTypeId={1} tableHeading="Invited Visitors Reports" />;
const WalkinVisitorTab = () => <VisitorTableTab visitorTypeId={2} tableHeading="Walk-in Visitor Reports" />;

const VisitorReports = () => {
    const tabs = [
        { key: "invited", label: "Invited Visitor", component: InvitedVisitorTab },
        { key: "walkin", label: "Walk-in Visitor", component: WalkinVisitorTab }
    ];

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto gap-4 pt-4 scrollbar-hide">
            <div className='flex flex-col gap-1 shrink-0'>
                <Breadcrumbs />
                <PageHeadingSection
                    mainHeading="Visitor Reports"
                    subHeading="Detailed log of all visitor records."
                />
            </div>

            <SectionTab tabs={tabs} defaultTab="invited" />
        </div>
    );
};

export default VisitorReports;
